// Multi-debt credit-card payoff amortization for resfi.
// Standard method (matches Bankrate/typical card-payoff calculators):
// interest accrues MONTHLY at APR/12, minimum payments are fixed dollar amounts,
// and the freed-up payment of a cleared card rolls into the remaining cards
// (total monthly outlay stays fixed) — distributed by the chosen strategy.

const MAX_MONTHS = 1200; // safety cap (100 years)
const EPS = 0.005;

// Standard monthly periodic rate: APR / 12.
function monthlyRate(apr) {
  return apr / 100 / 12;
}

// Priority order for distributing extra / rolled-over money:
// avalanche -> highest APR first; snowball -> lowest starting balance first.
function comparePriority(strategy) {
  return (a, b) =>
    strategy === "snowball"
      ? a.startBal - b.startBal || b.apr - a.apr
      : b.apr - a.apr || a.startBal - b.startBal;
}

// debts: [{ name, balance, apr, minPayment }]
// opts:  { strategy: 'avalanche'|'snowball', extraMonthly, extraYearly, oneTime, oneTimeMonth }
export function simulate(debts, opts = {}) {
  const {
    strategy = "avalanche",
    extraMonthly = 0,
    extraYearly = 0,
    oneTime = 0,
    oneTimeMonth = 0,
  } = opts;

  const work = debts.map((d, idx) => ({
    idx,
    name: d.name,
    bal: d.balance,
    startBal: d.balance,
    apr: d.apr,
    minPayment: d.minPayment,
    rate: monthlyRate(d.apr),
    totalInterest: 0,
    totalPaid: 0,
    payoffMonth: null,
    payments: [], // payment applied each month (index 0 = month 1)
  }));

  // Priority order for distributing extra / rolled-over money.
  const priority = [...work].sort(comparePriority(strategy));

  const fixedMinTotal = debts.reduce((s, d) => s + d.minPayment, 0);
  const startTotal = debts.reduce((s, d) => s + d.balance, 0);
  const firstInterest = work.reduce((s, d) => s + d.bal * d.rate, 0);
  const monthlyCommit = fixedMinTotal + extraMonthly;

  let month = 0;
  let totalPaid = 0;
  let totalInterest = 0;
  const schedule = [{ month: 0, balance: startTotal }];

  const active = () => work.filter((d) => d.bal > EPS);

  while (active().length > 0 && month < MAX_MONTHS) {
    month += 1;

    // 1. Accrue this month's interest on every active debt.
    for (const d of work) {
      if (d.bal > EPS) {
        const i = d.bal * d.rate;
        d.bal += i;
        totalInterest += i;
        d.totalInterest += i;
      }
    }

    // 2. Total budget stays fixed (minimums + extras), plus yearly / one-time.
    let budget = fixedMinTotal + extraMonthly;
    if (month % 12 === 0) budget += extraYearly;
    if (month === oneTimeMonth) budget += oneTime;

    const paidThis = work.map(() => 0);

    // 3. Apply each active debt's minimum payment (capped at its balance).
    let applied = 0;
    for (const d of active()) {
      const pay = Math.min(d.minPayment, d.bal);
      d.bal -= pay;
      totalPaid += pay;
      d.totalPaid += pay;
      paidThis[d.idx] += pay;
      applied += pay;
    }

    // 4. Distribute the remaining budget in fixed priority order.
    let remaining = budget - applied;
    if (remaining > EPS) {
      for (const d of priority) {
        if (remaining <= EPS) break;
        if (d.bal <= EPS) continue;
        const pay = Math.min(remaining, d.bal);
        d.bal -= pay;
        totalPaid += pay;
        d.totalPaid += pay;
        paidThis[d.idx] += pay;
        remaining -= pay;
      }
    }

    for (const d of work) {
      if (d.bal < EPS) d.bal = 0;
      d.payments.push(paidThis[d.idx]);
      if (d.bal <= EPS && d.payoffMonth === null && d.startBal > 0) {
        d.payoffMonth = month;
      }
    }
    schedule.push({ month, balance: work.reduce((s, d) => s + d.bal, 0) });
  }

  const neverPayoff = active().length > 0;

  const cards = work
    .filter((d) => d.startBal > 0)
    .map((d) => ({
      id: d.idx,
      name: d.name,
      months: d.payoffMonth,
      totalInterest: d.totalInterest,
      totalPaid: d.totalPaid,
      startBal: d.startBal,
      neverPayoff: d.payoffMonth === null,
      segments: buildSegments(d.payments),
    }));

  return {
    neverPayoff,
    months: neverPayoff ? null : month,
    totalPaid: neverPayoff ? null : totalPaid,
    totalInterest: neverPayoff ? null : totalInterest,
    principal: startTotal,
    schedule,
    firstInterest,
    monthlyCommit,
    minToProgress: firstInterest + 1,
    cards,
  };
}

// Compress a per-month payment array into constant-amount segments,
// e.g. [{ start: 1, end: 90, amount: 96 }, { start: 91, end: 91, amount: 113.92 }, ...].
// Trailing zero months (after the card is paid off) are ignored.
function buildSegments(payments) {
  const segs = [];
  let last = payments.length - 1;
  while (last >= 0 && payments[last] <= EPS) last--;
  let i = 0;
  while (i <= last) {
    const amt = payments[i];
    let j = i;
    while (j + 1 <= last && Math.abs(payments[j + 1] - amt) < 0.01) j++;
    segs.push({ start: i + 1, end: j + 1, amount: amt });
    i = j + 1;
  }
  return segs;
}

// Validate raw debt-input rows; returns { errors, valid } where `valid` is the
// list of well-formed debts ready for simulate(). Pure and unit-testable.
export function collectValidDebts(rows) {
  const num = (v) => (v === "" || v === null || v === undefined ? NaN : parseFloat(v));
  const errors = {};
  const valid = [];
  rows.forEach((d, i) => {
    const filled = d.name || d.balance || d.payment || d.rate;
    if (!filled) return;
    const bal = num(d.balance), pay = num(d.payment), rate = num(d.rate);
    if (Number.isNaN(bal) || bal <= 0) errors[`${i}-balance`] = "Required > 0";
    if (Number.isNaN(pay) || pay <= 0) errors[`${i}-payment`] = "Required > 0";
    if (Number.isNaN(rate) || rate < 0 || rate > 100) errors[`${i}-rate`] = "0–100";
    if (!errors[`${i}-balance`] && !errors[`${i}-payment`] && !errors[`${i}-rate`]) {
      valid.push({ name: d.name || `Debt ${i + 1}`, balance: bal, apr: rate, minPayment: pay });
    }
  });
  return { errors, valid };
}

export function formatCurrency(value, decimals = 2) {
  if (value === null || value === undefined || Number.isNaN(value)) return "$0.00";
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function monthsToYears(months) {
  if (months === null || months === undefined) return "";
  const y = Math.floor(months / 12);
  const m = months % 12;
  const parts = [];
  if (y > 0) parts.push(`${y} ${y === 1 ? "year" : "years"}`);
  if (m > 0) parts.push(`${m} ${m === 1 ? "month" : "months"}`);
  return parts.length ? parts.join(" ") : "0 months";
}

export function payoffDate(months) {
  if (months === null || months === undefined) return "—";
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// Human-readable line for a single payment-schedule segment.
export function formatSegmentLine(seg, index, total) {
  const prefix = index === 0 ? "Pay" : "then pay";
  const isLast = index === total - 1;
  const when = isLast
    ? `at month #${seg.end} to pay off.`
    : `until month #${seg.end}.`;
  return `${prefix} ${formatCurrency(seg.amount, 2)} ${when}`;
}

// Human-readable payment schedule lines for a card's segments.
export function scheduleLines(segments) {
  if (!segments || segments.length === 0) return [];
  return segments.map((s, i) => formatSegmentLine(s, i, segments.length));
}
